import { calculateArea, totalDistance } from "@nova/calculate-geospatial"
import { formatArea, formatDistance } from "@nova/format"
import { InfraredImageWithContext, isUsable } from "@nova/constants"
import {
    selectWorkspaceAreaUnit,
    selectWorkspaceDistanceUnit,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import {
    selectAllMergedFilteredHotspots,
    selectAllMergedFlights,
    selectAllMergedFlightUploads,
} from "./selectors"
import { selectMergedFlightById } from "features/entity-caching/mergedFlights/selectors"

export const useInspectorPanelMergedFlight = (entityId: string) => {
    const mergedFlight = useAppSelector(selectMergedFlightById(entityId))
    const { hotspotFlightIds } = mergedFlight || { hotspotFlightIds: [] }

    const filteredHotspots = useAppSelector(selectAllMergedFilteredHotspots(hotspotFlightIds))
    const uploads = useAppSelector(selectAllMergedFlightUploads(hotspotFlightIds))
    const mergedHotspotFlights = useAppSelector(selectAllMergedFlights(hotspotFlightIds))

    //when referencing the appearance, we just grab one of the flights and use that object, cause they should be all the same
    const appearanceReferenceFlight = mergedHotspotFlights[0]

    const areaUnit = useAppSelector(selectWorkspaceAreaUnit)
    const organizationSettingDistance = useAppSelector(selectWorkspaceDistanceUnit)

    const flightPathDistance = useMemo(() => {
        if (!uploads) return undefined
        const usableUploads = uploads.reduce((acc, cur) => {
            const upload = isUsable(cur)
            if (
                upload &&
                upload.takenAt &&
                upload.longitude !== undefined &&
                upload.latitude !== undefined
            )
                acc.push(upload as InfraredImageWithContext)
            return acc
        }, [] as InfraredImageWithContext[])

        const uploadsByFlight = usableUploads.reduce(
            (acc, curr) => {
                acc[curr.flightId] ??= []
                acc[curr.flightId]!.push(curr)
                return acc
            },
            {} as Record<string, InfraredImageWithContext[]>
        )

        const distances = Object.entries(uploadsByFlight).map(([, flightUploads]) =>
            totalDistance(
                flightUploads
                    .sort((a, b) => (a.takenAt || 0) - (b.takenAt || 0))
                    .map((image) => ({ longitude: image.longitude, latitude: image.latitude }))
            )
        )

        const distance = distances.reduce((prev, curr) => prev + curr, 0)

        const formattedDistance = formatDistance({
            value: distance,
            unit: organizationSettingDistance,
        })

        return formattedDistance
    }, [organizationSettingDistance, uploads])

    const formattedOrthoArea = useMemo(() => {
        const area = mergedHotspotFlights.reduce(
            (acc, flight) => acc + (flight?.job?.area || 0),
            0 as number
        )

        return area ? formatArea({ value: area, unit: areaUnit }) : undefined
    }, [areaUnit, mergedHotspotFlights])

    const formattedPolygonsArea = useMemo(() => {
        const area = filteredHotspots.reduce((area, hotspot) => {
            if (hotspot.polygon.length >= 3) area += calculateArea(hotspot.polygon)
            return area
        }, 0)

        return area ? formatArea({ value: area, unit: areaUnit, waterfall: true }) : undefined
    }, [filteredHotspots, areaUnit])

    if (!mergedFlight) return {}
    return {
        flightPathDistance,
        formattedOrthoArea,
        formattedPolygonsArea,
        appearanceReferenceFlight,
        mergedFlight,
        hotspotFlightIds,
    }
}
